import type { Client } from 'urql';

import {
  COMPOSITION_ENTITY_DATA,
  PositionsEntityKind,
} from '@revelio/data-access';

import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type TreeFilter,
} from '../types/filters';

const isNumericName = (name: string): boolean => /^\d+$/.test(name);

type UnresolvedCompanyEntry = {
  rcid: number;
};

export const collectUnresolvedCompanyRcids = (
  filters: Filter[]
): UnresolvedCompanyEntry[] => {
  const rcids = new Set<number>();

  for (const filter of filters) {
    const treeInstances: TreeFilter[] = [];

    if (filter.id === FilterId.COMPANY && filter.type === FilterType.TREE) {
      treeInstances.push(filter);
    } else if (filter.id === FilterId.MULTI_COMPANY && isMultiFilter(filter)) {
      for (const instance of filter.instances) {
        if (instance.type === FilterType.TREE) {
          treeInstances.push(instance);
        }
      }
    }

    for (const instance of treeInstances) {
      for (const sel of instance.selections) {
        // Only treat numeric names as RCIDs for company-level selections.
        // Industry selections (rics_k10 / rics_k400) with a numeric fallback
        // name are industry ids — looking them up as RCIDs mislabels the chip
        // with whatever company coincidentally shares that id.
        if (sel.levelId !== 'company') continue;
        if (isNumericName(sel.name)) {
          rcids.add(Number(sel.name));
        }
      }
    }
  }

  return [...rcids].map((rcid) => ({ rcid }));
};

export const resolveCompanyNamesFromApi = async (
  client: Client,
  rcids: UnresolvedCompanyEntry[]
): Promise<Map<string, string>> => {
  if (rcids.length === 0) return new Map();

  const entities = rcids.map((entry) => ({
    EntType: PositionsEntityKind.Company,
    EntID: entry.rcid,
  }));

  const result = await client
    .query(COMPOSITION_ENTITY_DATA, { entities })
    .toPromise();

  if (result.error) {
    console.warn(
      '[AI Search] resolveCompanyNamesFromApi: query error',
      result.error
    );
    return new Map();
  }

  const nameMap = new Map<string, string>();
  const responseEntities = result.data?.composition_v2?.entities ?? [];

  for (const entity of responseEntities) {
    const id = entity.metadata.id;
    const name = entity.metadata.long_name || entity.metadata.short_name || '';
    if (name) {
      nameMap.set(String(id), name);
    }
  }

  return nameMap;
};
